import * as React from "react";
import { useLazyGetFileDetailsQuery } from "@/api/files";
import { type UploadStatusMessage, useUploadStatus } from "@/hooks/use-upload-status";
import { TxType } from "@/types/transaction";
import { calculateUploadProgress, mapBackendStatusToFileStatus } from "./helpers";
import { getErrorMessage } from "./errorMessages";
import { advancePhaseEvent } from "./phaseLabels";
import { type BackendStatus, type FileWithProgress, type UpdateUploadRows } from "./types";

interface Props {
  getActiveTxIds: () => ReadonlySet<string>;
  updateRows: UpdateUploadRows;
}

const STATUS_ORDER: Record<FileWithProgress["status"], number> = {
  pending: 0,
  started: 1,
  uploading: 2,
  completed: 3,
  error: 3,
};

const advanceStatus = (
  current: FileWithProgress["status"],
  incoming: FileWithProgress["status"],
): FileWithProgress["status"] => (STATUS_ORDER[incoming] > STATUS_ORDER[current] ? incoming : current);

const applyBackendEvent = (row: FileWithProgress, event: UploadStatusMessage): FileWithProgress => {
  if (row.status === "completed" || row.status === "error") return row;

  const backendStatus = event.status as BackendStatus;
  const isFailed = backendStatus === "failed";
  const incomingStatus = isFailed ? "error" : mapBackendStatusToFileStatus(backendStatus, event.progress);
  return {
    ...row,
    progress: Math.max(calculateUploadProgress(event.progress, row.file.size), row.progress),
    status: isFailed ? "error" : advanceStatus(row.status, incomingStatus),
    backend_status: backendStatus,
    error_code: event.error_code || row.error_code,
    error: isFailed ? getErrorMessage(event.error_code) : row.error,
    current_event: advancePhaseEvent(row.current_event, event.current_event),
  };
};

export const UploadModalWebsocket: React.FC<Props> = ({ getActiveTxIds, updateRows }) => {
  const [loadFileDetails] = useLazyGetFileDetailsQuery();
  const filenameByFseRef = React.useRef(new Map<string, string>());

  const getBackendFilename = React.useCallback(
    async (fseId: string): Promise<string | undefined> => {
      const cached = filenameByFseRef.current.get(fseId);
      if (cached) return cached;
      try {
        const details = await loadFileDetails({ fse_id: fseId }).unwrap();
        if (typeof details.name !== "string" || details.name.length === 0) return undefined;
        filenameByFseRef.current.set(fseId, details.name);
        return details.name;
      } catch {
        return undefined;
      }
    },
    [loadFileDetails],
  );

  const onUploadStatusChange = React.useCallback(
    async (event: UploadStatusMessage) => {
      if (!getActiveTxIds().has(event.tx_id) || event.tx_type !== TxType.UPLOAD) return;

      if (event.type === "document") {
        for (const fseId of event.fse_ids) {
          const backendFilename = await getBackendFilename(fseId);
          if (!backendFilename) continue;
          updateRows(rows => {
            const matchingClientId = rows.find(
              row => row.tx_id === event.tx_id && row.upload_filename === backendFilename,
            )?.clientId;
            if (!matchingClientId) return rows;
            return rows.map(row =>
              row.clientId === matchingClientId ? { ...applyBackendEvent(row, event), fse_id: fseId } : row,
            );
          });
        }
        return;
      }

      if (event.type !== "transaction") return;
      const backendStatus = event.status as BackendStatus;
      updateRows(rows =>
        rows.map(row => {
          if (row.tx_id !== event.tx_id || row.status === "completed" || row.status === "error") return row;
          if (backendStatus === "failed") {
            return {
              ...row,
              status: "error",
              backend_status: "failed",
              error_code: event.error_code || row.error_code,
              error: getErrorMessage(event.error_code),
            };
          }
          const transactionProgress = calculateUploadProgress(event.progress, row.file.size);
          const progress = row.file.name.toLowerCase().endsWith(".zip")
            ? Math.max(row.progress, transactionProgress)
            : row.progress;
          const incomingStatus = progress >= 100 ? "completed" : "uploading";
          return {
            ...row,
            progress,
            status: advanceStatus(row.status, incomingStatus),
            current_event: advancePhaseEvent(row.current_event, event.current_event),
          };
        }),
      );
    },
    [getActiveTxIds, getBackendFilename, updateRows],
  );

  useUploadStatus(onUploadStatusChange);

  return null;
};
